/**
 * palette.js — categorical colour sets
 *
 * Two kinds of colour live in this app and they must not be confused:
 *
 *   SEMANTIC   green / amber / red carry fixed meaning (healthy, warning,
 *              failed). Those live in App.css as --success / --warning /
 *              --danger and are NOT defined here — a driver seeing red
 *              expects "problem" regardless of what the brand colour is.
 *
 *   CATEGORICAL  arbitrary slots that only need to be told apart — place
 *              types, vendor types, chart series, route indices. These have
 *              no inherent meaning, so they should sit with the brand.
 *
 * The categorical set below is deliberately muted and earthy rather than the
 * saturated primary ramp (bright blue / green / red / purple) that ships as
 * the default in every charting library. That default is the single loudest
 * "generic template" signal in a UI, and its slot-1 blue actively fought the
 * orange brand.
 *
 * Every `text` value is verified at >= 4.5:1 (WCAG AA) against both #ffffff
 * and the #f8f7f5 cream surface. The `pin` values are for fills and markers
 * only — brand orange is 2.57:1 on cream and must never be used as text,
 * which is why `text` carries a deepened variant.
 */

// Ordered categorical ramp. Index 0 is the brand, so the most common series
// in any chart reads as "ours" rather than as an arbitrary blue.
export const CATEGORICAL = [
  { pin: '#e8820c', text: '#a65d09' }, // brand orange
  { pin: '#2f6f6a', text: '#255854' }, // deep teal
  { pin: '#7d5088', text: '#63406c' }, // muted plum
  { pin: '#5c7a3f', text: '#465e30' }, // moss
  { pin: '#b4553a', text: '#8f4230' }, // terracotta
  { pin: '#4a5568', text: '#3a4351' }, // slate
];

/** Fills only — for chart series, bars, donut segments. */
export const CHART_COLORS = CATEGORICAL.map((c) => c.pin);

/** Neutral fallback for any unrecognised category. */
export const DEFAULT_CATEGORY = { pin: '#4a5568', text: '#3a4351' };

/**
 * Place types. Previously duplicated verbatim in both Orders.js and
 * TrackingMap.js, which meant the two maps could drift apart silently —
 * they now share this one definition.
 */
export const PLACE_TYPE_COLORS = {
  Depot: CATEGORICAL[0],
  Terminal: CATEGORICAL[2],
  Delivery: CATEGORICAL[3],
  Pickup: CATEGORICAL[4],
  Warehouse: CATEGORICAL[1],
  Office: CATEGORICAL[5],
  'Fuel Station': CATEGORICAL[4],
};

export const VENDOR_TYPE_COLORS = {
  Transport: CATEGORICAL[0],
  Supplier: CATEGORICAL[1],
  Maintenance: CATEGORICAL[3],
};

/** Distinct enough to tell apart when several routes overlay one map. */
export const ROUTE_COLORS = [CATEGORICAL[0].pin, CATEGORICAL[1].pin, CATEGORICAL[2].pin];

/**
 * Geofence crossings. Enter and Exit are the only two values the API returns,
 * and confusing them is the expensive mistake — a vehicle leaving a depot and
 * a vehicle arriving at one demand opposite responses. So they must never look
 * alike anywhere they appear: the history table, the notification panel, the
 * map.
 *
 * Categorical, not semantic, and deliberately so. Leaving a fence is not a
 * failure and entering one is not a success — a delivery van is supposed to
 * leave. Reaching for --success / --danger here would spend the two colours
 * this app reserves for health on a pair of states that carry no health
 * meaning at all, and would tell a dispatcher that every departure is a
 * problem. Teal and terracotta sit far enough apart to read instantly,
 * including for the red-green colour blind, for whom a success/danger pair is
 * the one distinction that does not survive.
 */
export const GEOFENCE_EVENT_COLORS = {
  Enter: CATEGORICAL[1],   // deep teal
  Exit:  CATEGORICAL[4],   // terracotta
};

/**
 * Fill for a fence with no colour of its own. Matches the violet the map's
 * draw tool already uses while a shape is being drawn (TrackingMap.js), so a
 * fence does not change colour the moment it is saved.
 */
export const GEOFENCE_DEFAULT_COLOR = '#8b5cf6';
